import React, {MutableRefObject, ReactNode, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Elements, PaymentElement, useElements, useStripe} from '@stripe/react-stripe-js'
import type {Stripe, StripeElements, StripePaymentElementChangeEvent} from '@stripe/stripe-js'
import type {ApprovedPaymentDetail, PaymentComponentDescriptor, PaymentMethodType} from '@/Types/PaymentComponent'
import {
    AcceptedMethods,
    allowedMethodsAttribute,
    isStripeApproved,
    normalizeRainforestApproved,
    normalizeStripeApproved,
    preloadPaymentSdk,
    rainforestDeclineReason,
    rainforestUpdatedMethodType,
    stripeIntentKind,
    stripeMethodType,
    stripeInstanceFor,
    stripePaymentElementOptions,
    type StripeConfirmedIntent,
    type StripePaymentElementOptions,
} from '@/Utils/paymentComponent'
import {subscribeToRainforestXhrErrors} from '@/Utils/rainforestXhrErrors'
import Button from '@/Components/Buttons/Button'

export type {AcceptedMethods}

/**
 * The normalized callback surface (design doc §4). `onError`,
 * `onCardBrandUpdated`, and `onMethodTypeChanged` extend the doc's core
 * six — they carry widget events the hand-rolled implementations this hook
 * replaced were relying on (StepPayment's error/card-brand handling,
 * PersonUpdatePaymentMethod's method preselection).
 */
export interface PaymentComponentCallbacks {
    onLoaded?: () => void
    onValid?: () => void
    onInvalid?: () => void
    onAttempted?: () => void
    onDeclined?: (reason: string | null) => void
    onApproved: (detail: ApprovedPaymentDetail) => void
    onError?: (event: Event) => void
    onCardBrandUpdated?: () => void
    onMethodTypeChanged?: (methodType: PaymentMethodType) => void
}

export interface PaymentComponentOptions {
    useNativeButton?: boolean
    hideErrorMessage?: boolean
    /**
     * Omit to render the widget without an `allowed-methods` attribute —
     * the processor's own default method set (what StepPayment always
     * relied on). An all-false set renders a literal `allowed-methods=""`.
     */
    acceptedMethods?: AcceptedMethods
    /** Moves this method to the front of `allowed-methods`, making it the widget's initially selected tab. */
    preferredMethod?: 'CARD' | 'ACH'
}

export interface PaymentComponentHook {
    paymentComponent: ReactNode | null
    paymentComponentLoaded: boolean
    paymentValid: boolean
    processPayment: () => Promise<void>
    paymentProcessing: boolean
}

/** The live Elements instances, published by the Elements-scoped child. */
interface StripeInstances {
    stripe: Stripe
    elements: StripeElements
}

interface StripeCaptureProps {
    instancesRef: MutableRefObject<StripeInstances | null>
    callbacksRef: MutableRefObject<PaymentComponentCallbacks>
    methodTypeRef: MutableRefObject<PaymentMethodType>
    setLoaded: (loaded: boolean) => void
    setValid: (valid: boolean) => void
    elementOptions?: StripePaymentElementOptions
    /** Renders this branch's own submit button — see {@see PaymentComponentOptions.useNativeButton}. */
    useNativeButton: boolean
    onSubmit: () => Promise<void>
}

/**
 * The Stripe counterpart to the Rainforest branch's `bindElement` listeners.
 * It exists as a child component rather than inline hook code because
 * `useStripe()`/`useElements()` only resolve underneath <Elements>, and
 * processPayment() needs both — so it publishes them to a ref the hook owns.
 *
 * `ready` and `change` are the only two events PaymentElement raises;
 * everything else in the normalized surface (attempted/approved/declined)
 * comes from the confirm call, not from the element.
 */
function StripeCapture({
    instancesRef,
    callbacksRef,
    methodTypeRef,
    setLoaded,
    setValid,
    elementOptions,
    useNativeButton,
    onSubmit,
}: StripeCaptureProps): ReactNode {
    const stripe = useStripe()
    const elements = useElements()

    /**
     * The last `complete` reported. PaymentElement raises `change` on every
     * keystroke, whereas the Rainforest widget only raises valid/invalid on
     * a transition — gating on this keeps the callback surface identical
     * instead of firing onValid dozens of times per card number.
     */
    const completeRef = useRef<boolean | null>(null)

    /**
     * The button's own copies of valid/processing. They cannot be the hook's
     * state: the widget JSX is memoized on the descriptor, so a re-render
     * driven by the hook's state returns the same cached element and never
     * reaches this child.
     */
    const [complete, setComplete] = useState<boolean>(false)
    const [submitting, setSubmitting] = useState<boolean>(false)

    useEffect(() => {
        instancesRef.current = stripe != null && elements != null ? {stripe, elements} : null

        return () => {
            instancesRef.current = null
        }
    }, [stripe, elements, instancesRef])

    const handleReady = (): void => {
        console.debug('Payment component loaded')
        callbacksRef.current.onLoaded?.()
        setLoaded(true)
    }

    const handleChange = (event: StripePaymentElementChangeEvent): void => {
        const methodType = stripeMethodType(event.value.type)

        if (methodType != null && methodType !== methodTypeRef.current) {
            methodTypeRef.current = methodType
            callbacksRef.current.onMethodTypeChanged?.(methodType)
        }

        if (event.complete === completeRef.current) {
            return
        }

        completeRef.current = event.complete
        setComplete(event.complete)

        if (event.complete) {
            console.info('Valid payment')
            callbacksRef.current.onValid?.()
            setValid(true)
        } else {
            console.info('Invalid payment')
            callbacksRef.current.onInvalid?.()
            setValid(false)
        }
    }

    const handleSubmit = async (): Promise<void> => {
        setSubmitting(true)

        try {
            await onSubmit()
        } finally {
            setSubmitting(false)
        }
    }

    // The wrapper carries the same data-testid as <rainforest-payment> so
    // one E2E selector finds payment capture under either processor.
    return <div data-testid={'payment-component'}>
        <PaymentElement
            options={elementOptions}
            onReady={handleReady}
            onChange={handleChange}
        />
        {/*
            PaymentElement ships no submit button of its own, so the branch
            renders one where the Rainforest widget supplied its own. Without
            it, the three pages that never call processPayment() —
            TicketingPurchase, RegistrationForm, RegistrationPaymentLink —
            would show a card form with nothing to click.
        */}
        {useNativeButton && <Button
            size={'lg'}
            flex={true}
            disabled={!complete || submitting}
            onClick={() => { void handleSubmit() }}
            testId={'payment-component-submit'}
            classes={['mt-4', 'w-full', 'justify-center']}
        >
            {submitting ? 'Processing…' : 'Pay'}
        </Button>}
    </div>
}

/**
 * One processor-keyed payment capture hook (ENG-4089, design doc §4),
 * replacing useRainforest plus the hand-rolled <rainforest-payment> copies
 * in StepPayment and PersonUpdatePaymentMethod. The descriptor comes from
 * the page's controller (Inertia prop or JSON response `paymentComponent`);
 * internals switch on `descriptor.processor` — Rainforest renders
 * <rainforest-payment>, Stripe renders <Elements><PaymentElement/></Elements>
 * (ENG-4125). Rainforest's browser SDK is lazily injected from
 * `descriptor.sdkUrl` on first mount; Stripe's comes from loadStripe().
 *
 * Passing null tears the component down so its disconnectedCallback fires
 * and Rainforest releases any active Apple Pay session.
 */
export function usePaymentComponent(
    descriptor: PaymentComponentDescriptor | null,
    callbacks: PaymentComponentCallbacks,
    options: PaymentComponentOptions = {},
): PaymentComponentHook {
    const [loaded, setLoaded] = useState<boolean>(false)
    const [valid, setValid] = useState<boolean>(false)
    const [processing, setProcessing] = useState<boolean>(false)

    /**
     * Callbacks are invoked through a ref so listeners — which only rebind
     * when the widget element itself is remounted — always run the latest
     * render's closures.
     */
    const callbacksRef = useRef(callbacks)

    useEffect(() => {
        callbacksRef.current = callbacks
    })

    /** The live widget element this hook instance rendered, for processPayment(). */
    const elementRef = useRef<Element | null>(null)

    /** The Stripe branch's equivalent of elementRef, published by StripeCapture. */
    const stripeInstancesRef = useRef<StripeInstances | null>(null)

    /**
     * The method type PaymentElement last reported. The confirmed intent
     * comes back with an unexpanded `payment_method`, so the `change` event
     * is the only place the selected method is knowable client-side.
     */
    const methodTypeRef = useRef<PaymentMethodType>('CARD')

    useEffect(() => {
        if (descriptor?.processor !== 'rainforest') {
            return
        }

        return subscribeToRainforestXhrErrors((request) => {
            console.error('Rainforest loading error', request)
        })
    }, [descriptor?.processor])

    useEffect(() => {
        if (!descriptor) {
            // The derived component is already null; reset the loaded/valid
            // flags like the pre-hook implementations did on teardown.
            setLoaded(false)
            setValid(false)

            return
        }

        if (descriptor.processor === 'rainforest' || descriptor.processor === 'stripe') {
            preloadPaymentSdk(descriptor)

            return
        }

        console.error('Unsupported payment processor', descriptor.processor)
        setLoaded(false)
        setValid(false)
    }, [descriptor])

    /**
     * Memoized on the credentials rather than built in render: <Elements>
     * latches the first `stripe` prop it is given and warns on a changed
     * one. stripeInstanceFor() hands back the instance the page's
     * mount-time preload already started (ENG-4689).
     *
     * `{stripeAccount}` is not optional under Connect direct charges — a
     * client secret minted on the connected account will not resolve
     * against a platform-scoped instance (ENG-4124).
     */
    const stripePromise = useMemo((): Promise<Stripe | null> | null => {
        if (descriptor?.processor !== 'stripe') {
            return null
        }

        return stripeInstanceFor(descriptor)
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [descriptor?.processor, descriptor?.publishableKey, descriptor?.merchantId])

    /**
     * Binds the widget's nine events on the element this hook instance
     * actually rendered — a ref callback with cleanup rather than a
     * document-level querySelector, so two widgets on one page can never
     * cross-wire. React re-runs it (cleanup included) when the element
     * remounts on a session change.
     */
    const bindElement = useCallback((element: Element | null): (() => void) | undefined => {
        if (element == null) {
            return undefined
        }

        elementRef.current = element

        const handleLoaded = (e: Event): void => {
            callbacksRef.current.onLoaded?.()
            setLoaded(true)
            console.debug('Payment component loaded', e)
        }

        const handleCardBrandUpdated = (e: Event): void => {
            console.debug('Card brand updated', e)
            callbacksRef.current.onCardBrandUpdated?.()
        }

        const handleValid = (e: Event): void => {
            console.info('Valid payment', e)
            callbacksRef.current.onValid?.()
            setValid(true)
        }

        const handleInvalid = (e: Event): void => {
            console.info('Invalid payment', e)
            callbacksRef.current.onInvalid?.()
            setValid(false)
        }

        const handleAttempted = (e: Event): void => {
            console.info('Payment attempted', e)
            callbacksRef.current.onAttempted?.()
            setProcessing(true)
        }

        const handleDeclined = (e: Event): void => {
            console.info('Declined payment', e)
            callbacksRef.current.onDeclined?.(rainforestDeclineReason(e))
            setProcessing(false)
        }

        const handleError = (e: Event): void => {
            console.error('Payment component error', e)
            callbacksRef.current.onError?.(e)
        }

        const handleMethodUpdated = (e: Event): void => {
            const methodType = rainforestUpdatedMethodType(e)

            if (methodType != null) {
                callbacksRef.current.onMethodTypeChanged?.(methodType)
            }
        }

        const handleApproved = (e: Event): void => {
            console.info('Approved payment', e)
            setProcessing(false)

            const detail = normalizeRainforestApproved(e)

            if (detail == null) {
                console.error('Approved payment event carried no payload', e)

                return
            }

            callbacksRef.current.onApproved(detail)
        }

        element.addEventListener('loaded', handleLoaded)
        element.addEventListener('card-brand-updated', handleCardBrandUpdated)
        element.addEventListener('valid', handleValid)
        element.addEventListener('invalid', handleInvalid)
        element.addEventListener('attempted', handleAttempted)
        element.addEventListener('declined', handleDeclined)
        element.addEventListener('approved', handleApproved)
        element.addEventListener('error', handleError)
        element.addEventListener('method-updated', handleMethodUpdated)

        return () => {
            element.removeEventListener('loaded', handleLoaded)
            element.removeEventListener('card-brand-updated', handleCardBrandUpdated)
            element.removeEventListener('valid', handleValid)
            element.removeEventListener('invalid', handleInvalid)
            element.removeEventListener('attempted', handleAttempted)
            element.removeEventListener('declined', handleDeclined)
            element.removeEventListener('approved', handleApproved)
            element.removeEventListener('error', handleError)
            element.removeEventListener('method-updated', handleMethodUpdated)

            if (elementRef.current === element) {
                elementRef.current = null
            }
        }
    }, [])

    /**
     * Stripe has no widget-side submit: confirming the intent is what
     * captures, so this one call covers what the Rainforest widget spreads
     * across its `attempted`, `approved`, and `declined` events.
     *
     * `redirect: 'if_required'` keeps the payer on the page for card and
     * ACH — the only methods this app offers — so the promise resolves with
     * the confirmed intent instead of navigating away.
     */
    const processStripe = async (intentId: string): Promise<void> => {
        const instances = stripeInstancesRef.current

        if (instances == null) {
            /**
             * Unlike the Rainforest ref callback, which is bound the moment
             * the element renders, useStripe()/useElements() resolve only
             * after Stripe.js loads — so this is a reachable race, not a
             * can't-happen. It has to report a decline: callers set their own
             * processing flag before calling (StepPayment.submitStep), and a
             * bare return leaves them spinning with no way back.
             */
            console.error('Payment component not found')
            callbacksRef.current.onDeclined?.(null)

            return
        }

        const {stripe, elements} = instances

        callbacksRef.current.onAttempted?.()
        setProcessing(true)

        try {
            // A SetupIntent (the card-entry flow) must be confirmed with
            // confirmSetup(); confirmPayment() rejects one outright.
            const {intent, error} = stripeIntentKind(intentId) === 'setup'
                ? await stripe.confirmSetup({elements, redirect: 'if_required'})
                    .then((result) => ({intent: result.setupIntent, error: result.error}))
                : await stripe.confirmPayment({elements, redirect: 'if_required'})
                    .then((result) => ({intent: result.paymentIntent, error: result.error}))

            setProcessing(false)

            if (error != null) {
                console.info('Declined payment', error)
                callbacksRef.current.onDeclined?.(error.message ?? null)

                return
            }

            if (intent == null || !isStripeApproved(intent.status)) {
                console.error('Confirmed intent did not reach an approved status', intent)
                callbacksRef.current.onDeclined?.(null)

                return
            }

            console.info('Approved payment', intent)
            callbacksRef.current.onApproved(
                normalizeStripeApproved(intent as StripeConfirmedIntent, methodTypeRef.current),
            )
        } catch (error) {
            console.error(error)
            setProcessing(false)
            callbacksRef.current.onDeclined?.(null)
        }
    }

    // The widget JSX is derived in render, memoized on the descriptor alone:
    // options are captured when the descriptor changes — the same
    // frozen-at-build semantics the state-held JSX had — and the `key`
    // forces React to unmount the prior <rainforest-payment> when the
    // session changes, instead of mutating attributes on the same DOM node.
    // Without that, Rainforest never tears down the previous Apple Pay
    // session and the next tap throws `InvalidAccessError: Page already has
    // an active payment session`.
    const component = useMemo((): ReactNode | null => {
        if (!descriptor) {
            return null
        }

        const {
            useNativeButton = false,
            hideErrorMessage = false,
            acceptedMethods,
            preferredMethod,
        } = options

        if (descriptor.processor === 'stripe') {
            if (stripePromise == null) {
                return null
            }

            // Keyed like the Rainforest branch: a new session must remount
            // <Elements> rather than mutate it, because its client secret is
            // latched at mount and a stale one silently fails to confirm.
            return <Elements
                key={descriptor.sessionKey}
                stripe={stripePromise}
                options={{clientSecret: descriptor.sessionKey}}
            >
                <StripeCapture
                    instancesRef={stripeInstancesRef}
                    callbacksRef={callbacksRef}
                    methodTypeRef={methodTypeRef}
                    setLoaded={setLoaded}
                    setValid={setValid}
                    elementOptions={stripePaymentElementOptions(acceptedMethods, preferredMethod)}
                    useNativeButton={useNativeButton}
                    onSubmit={() => processStripe(descriptor.intentId)}
                />
            </Elements>
        }

        if (descriptor.processor !== 'rainforest') {
            return null
        }

        // @ts-expect-error This is not an error.
        return <rainforest-payment
            key={descriptor.sessionKey}
            ref={bindElement}
            allowed-methods={acceptedMethods == null
                ? undefined
                : allowedMethodsAttribute(acceptedMethods, preferredMethod)}
            session-key={descriptor.sessionKey}
            payin-config-id={descriptor.intentId}
            hide-button={!useNativeButton}
            hide-error-message={hideErrorMessage}
            style-font-family={'Plus Jakarta Sans, -apple-system, system-ui, BlinkMacSystemFont, "Segoe UI", Roboto, Ubuntu'}
            style-border-color={'#D3E0F8'}
            style-font-size={'14px'}
            style-container-padding={'0'}
            data-testid={'payment-component'}
        />
    }, [descriptor, stripePromise])

    const process = async () => {
        if (descriptor?.processor === 'stripe') {
            return processStripe(descriptor.intentId)
        }

        try {
            const element = elementRef.current

            if (element) {
                await new Promise<void>((resolve) => {
                    element.addEventListener('submit', () => resolve(), {once: true})

                    // @ts-expect-error This is erroring just because we're not familiar with the Rainforest API
                    element.submit()
                })
            } else {
                console.error('Payment component not found')
            }
        } catch (error) {
            console.error(error)
        }
    }

    return {
        paymentComponent: component,
        paymentComponentLoaded: loaded,
        paymentValid: valid,
        processPayment: process,
        paymentProcessing: processing,
    }
}
